// dashboard/buildFindingsNarrative.js
//
// Composes a survey_evaluation_runs row's findings/clusters (the vNext
// engine's structured "what changed" output — supabase/functions/_shared/
// clinicalScoring.ts's Finding/ClusterMatch) into a short, clinic-facing
// "why" narrative: 2-3 lines naming the specific things that moved,
// instead of a bare score or a generic "condition is in the critical
// range" message (release-blocker #2, docs/BUILD_STATE.md).
//
// Severity ordering: immediate_review findings/clusters first, then red,
// then yellow, then finding_only (context markers like a missed
// medication dose) last — the reader should see the most clinically
// significant line first, capped at MAX_LINES total.
//
// Cluster de-duplication: when a cluster fires, its own reason_text_key
// is normally more specific/informative than its component findings'
// individual lines (e.g. "blood in urine together with urinating outside
// the normal location" vs. two separate weaker lines) — so a finding
// already named in a fired cluster's component_findings is dropped from
// the individual-findings list, and the cluster's own line stands in for
// it.

import { findingReasonText } from './findingReasonText';

const MAX_LINES = 3;
const EFFECT_RANK = { immediate_review: 0, red: 1, yellow: 2, finding_only: 3 };

function rank(effect) {
  return EFFECT_RANK[effect] ?? 4;
}

// findings: Finding[] | null/undefined, clusters: ClusterMatch[] | null/undefined
export function buildFindingsNarrative(findings, clusters) {
  const safeFindings = Array.isArray(findings) ? findings : [];
  const safeClusters = Array.isArray(clusters) ? clusters : [];

  const clusteredFindingCodes = new Set(safeClusters.flatMap(c => c.component_findings || []));

  const clusterLines = safeClusters
    .map(c => ({ effect: c.effect, text: findingReasonText(c.reason_text_key) }))
    .filter(l => l.text !== null);

  const findingLines = safeFindings
    .filter(f => !clusteredFindingCodes.has(f.finding_code))
    .map(f => ({ effect: f.effect, text: findingReasonText(f.reason_text_key) }))
    .filter(l => l.text !== null);

  const allLines = [...clusterLines, ...findingLines]
    .sort((a, b) => rank(a.effect) - rank(b.effect));

  // De-dupe identical text (two different questions can legitimately
  // produce the same plain-English observation, e.g. two "not eating"-
  // style rules across different signal groups) — keep first occurrence,
  // which is already the highest-severity thanks to the sort above.
  const seen = new Set();
  const deduped = [];
  for (const line of allLines) {
    if (seen.has(line.text)) continue;
    seen.add(line.text);
    deduped.push(line.text);
    if (deduped.length >= MAX_LINES) break;
  }

  return deduped;
}

// True only when there's real vNext findings data to show — callers use
// this to decide whether to prefer the vNext narrative over the existing
// clinicalSummary/alert.message fallback for conditions/submissions that
// predate vNext content, or have nothing to report.
export function hasFindingsNarrative(findings, clusters) {
  return buildFindingsNarrative(findings, clusters).length > 0;
}
